// src/automation/api/automation_data.js
//
// API layer for the Automation product.
//
// IMPORTANT: the automation backend (lambda_automations) is NOT a REST API with
// one route per action. It is a single dispatcher Lambda behind API Gateway that
// takes a POST with a JSON body naming a "task". The body shape is:
//
//   { "task": "list" }                         -> { success, tasks: [ ...names ] }
//   { "task": "config", "name": "vat_rapid" }  -> { success, task, config: {...} }
//   { "task": "<task_name>", "dry_run": true } -> runs the task, returns a result
//   { "task": "selftest" }                     -> read-only health check
//
// It shares the same Cognito Bearer authorizer as lambda_wbr, so we reuse the
// shared getAuthHeaders()/handleResponse() helpers (Authorization: Bearer <id_token>).
//
// The dispatcher lives at its own API Gateway endpoint. Until that endpoint is
// known/deployed, AUTOMATION_URL falls back to the shared WBR BASE_URL + /automation.
// Point REACT_APP_AUTOMATION_URL at the real dispatcher invoke URL when deployed.

import { getAuthHeaders, handleResponse } from "shared/api/function_data";

// The single dispatcher endpoint — a dedicated HTTP API (POST /dispatch route,
// $default stage), separate from the WBR BASE_URL gateway. Override via env for
// other environments.
export const AUTOMATION_URL =
  process.env.REACT_APP_AUTOMATION_URL ||
  "https://44h8drl95h.execute-api.eu-west-1.amazonaws.com/dispatch";

/**
 * POST a command to the dispatcher and return the parsed JSON result.
 * The dispatcher always answers with a JSON object; on transport/HTTP failure
 * we throw so callers (React Query) can surface the error.
 *
 * @param {object} command - e.g. { task: "list" } or { task: "config", name }
 * @returns {Promise<object>} the dispatcher result object
 */
// Signed-in login, attached to every request so the backend can check
// permissions and record who did what in the activity log.
let _apiLogin = "";
export const setApiLogin = (login) => {
  _apiLogin = login && login !== "User" ? String(login) : "";
};

async function dispatch(command) {
  const body = _apiLogin && !command.login ? { ...command, login: _apiLogin } : command;
  const response = await fetch(AUTOMATION_URL, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  handleResponse(response); // handles 401 -> re-login

  // The dispatcher returns 200 on success and 400/500 on failure, but it always
  // includes a JSON body with { success, summary, ... }. Prefer the body.
  let data = null;
  try {
    data = await response.json();
  } catch (_) {
    data = null;
  }

  if (!response.ok && !data) {
    throw new Error(`Automation request failed (HTTP ${response.status})`);
  }
  return data || { success: false, summary: `HTTP ${response.status}` };
}

// ─────────────────────────────────────────────────────────────────────────────
// Task grouping — CONFIG-DRIVEN
//
// Each task's config JSON carries its sidebar grouping:
//   { "group": "KYC Inhouse", "group_order": 1, "order": 1, ... }
// The sidebar is built from those fields: tasks sharing a `group` are collected
// under one accordion group; groups are sorted by `group_order` and tasks within
// a group by `order`. The backend `{task:"list"}` only returns names, so we fetch
// each task's config to read its group (cached per session).
//
// Fallback: if a task's config is missing or lacks `group`, it falls back to a
// prefix-derived group so the UI never breaks.
// ─────────────────────────────────────────────────────────────────────────────

// Prefix fallback used only when a config has no explicit `section`.
const PREFIX_FALLBACK = [
  { prefix: "forecast", section: "Forecast", section_order: 3 },
  { prefix: "kyc", section: "Queue Status Reports", section_order: 1 },
  { prefix: "vat", section: "Rapid", section_order: 2 },
  { prefix: "svr", section: "Queue Status Reports", section_order: 1 },
];

function fallbackSection(taskName) {
  const m = PREFIX_FALLBACK.find(
    (g) => taskName.startsWith(g.prefix + "_") || taskName === g.prefix
  );
  return m
    ? { section: m.section, section_order: m.section_order }
    : { section: "Other", section_order: 999 };
}

/** A URL-safe key from a section name, used as the accordion section key. */
export function sectionKeyFromName(name) {
  return String(name || "other")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** "kyc_core_mail" -> "Kyc Core Mail" (fallback display name if config lacks label). */
export function prettyTaskLabel(taskName) {
  const parts = String(taskName || "").split("_");
  return parts.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/**
 * Build the SECTION -> task structure for the sidebar from task descriptors:
 *   descriptors: [{ name, section, section_order, order, label }]
 * Returns [{ key, label, section_order, tasks: [{ name, label, order }] }]
 * sorted by section_order, then tasks within a section by order.
 * (No "group" level — sections contain tasks directly.)
 */
export function buildSections(descriptors = []) {
  const byName = new Map(); // sectionName -> { key, label, section_order, tasks: [] }

  for (const d of descriptors) {
    const fb = fallbackSection(d.name);
    const sectionName = d.section || fb.section;
    if (!byName.has(sectionName)) {
      byName.set(sectionName, {
        key: sectionKeyFromName(sectionName),
        label: sectionName,
        section_order:
          typeof d.section_order === "number" ? d.section_order : fb.section_order,
        tasks: [],
      });
    }
    const s = byName.get(sectionName);
    if (typeof d.section_order === "number") s.section_order = d.section_order;
    s.tasks.push({
      name: d.name,
      label: d.label || prettyTaskLabel(d.name),
      order: typeof d.order === "number" ? d.order : 999,
      hidden: !!d.hidden,
      enabled: d.enabled !== false,
      minutes_saved_per_run: Number(d.minutes_saved_per_run) || 0,
      // Custom = registered from the UI (no code handler) -> deletable.
      custom: !!d.custom,
    });
  }

  const sections = Array.from(byName.values());
  sections.sort(
    (a, b) => a.section_order - b.section_order || a.label.localeCompare(b.label)
  );
  for (const s of sections) {
    s.tasks.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }
  return sections;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * List dispatchable tasks. POST { task: "list" } -> { success, tasks: [...] }.
 * Returns the raw array of task-name strings (empty array on failure so the
 * landing page never breaks).
 */
export const getAutomationTasks = async () => {
  try {
    return (await fetchTaskList()).names;
  } catch (err) {
    console.warn("getAutomationTasks failed, returning empty list:", err?.message);
    return [];
  }
};

// {task:"list"} -> { names, builtin:Set|null }. `builtin` = tasks with a code
// handler; anything else was registered from the UI (custom, deletable).
async function fetchTaskList() {
  const data = await dispatch({ task: "list" });
  const names = data && Array.isArray(data.tasks) ? data.tasks : [];
  const builtin = data && Array.isArray(data.builtin) ? new Set(data.builtin) : null;
  return { names, builtin };
}

/**
 * List tasks already grouped for the sidebar, CONFIG-DRIVEN.
 *
 * Steps:
 *   1. {task:"list"} -> task names
 *   2. for each name, {task:"config"} -> read section/section_order/order/label (parallel)
 *   3. buildSections() -> sorted section -> task structure
 *
 * Returns [{ key, label, section_order, tasks: [{ name, label, order }] }].
 * Any task whose config fails to load falls back to prefix-based sectioning so
 * the sidebar still renders.
 */
export const getAutomationGroups = async () => {
  let names = [];
  let builtin = null;
  try {
    ({ names, builtin } = await fetchTaskList());
  } catch (err) {
    console.warn("getAutomationGroups: list failed:", err?.message);
  }
  if (names.length === 0) return [];

  const descriptors = await Promise.all(
    names.map(async (name) => {
      const cfg = await getTaskConfig(name); // null on failure
      return {
        name,
        section: cfg?.section,
        section_order: cfg?.section_order,
        order: cfg?.order,
        label: cfg?.label, // sidebar display name from config
        hidden: !!cfg?.hidden,
        enabled: cfg?.enabled,
        minutes_saved_per_run: cfg?.minutes_saved_per_run,
        custom: builtin ? !builtin.has(name) : false,
      };
    })
  );

  return buildSections(descriptors);
};

/**
 * One-shot console bootstrap. POST { task: "bootstrap", login, runs_limit }.
 * Returns everything the landing view needs in a single round-trip:
 *   { tasks:[...], configs:{name:cfg|null}, summary_map, runs, permissions }
 *
 * This replaces the first-load burst (list + one config-per-task + run_summary +
 * list_runs + get_permissions = ~15 calls) with ONE call. The returned pieces
 * are seeded into the React Query cache by the page so the individual endpoints
 * still work for targeted refetches.
 *
 * Returns a normalized object; falls back to empty structures on failure so the
 * page still renders (the individual queries then fetch on their own).
 */
export const getBootstrap = async (login = "", { runsLimit = 2000 } = {}) => {
  const data = await dispatch({ task: "bootstrap", login: login || undefined, runs_limit: runsLimit });
  if (!data || !data.success) {
    return { sections: null, summaryMap: {}, runs: [], permissions: null, configs: {} };
  }
  const configs = data.configs || {};
  const names = Array.isArray(data.tasks) ? data.tasks : Object.keys(configs);
  const builtin = Array.isArray(data.builtin) ? new Set(data.builtin) : null;
  const descriptors = names.map((name) => {
    const cfg = configs[name] || null;
    return {
      name,
      section: cfg?.section,
      section_order: cfg?.section_order,
      order: cfg?.order,
      label: cfg?.label,
      hidden: !!cfg?.hidden,
      enabled: cfg?.enabled,
      minutes_saved_per_run: cfg?.minutes_saved_per_run,
      custom: builtin ? !builtin.has(name) : false,
    };
  });
  return {
    sections: buildSections(descriptors),
    summaryMap: data.summary_map && typeof data.summary_map === "object" ? data.summary_map : {},
    runs: Array.isArray(data.runs) ? data.runs : [],
    permissions: data.permissions || null,
    configs,
  };
};

/**
 * Drop hidden tasks (and any section left empty) — used for the sidebar/cover so
 * hidden automations don't show for anyone. The Admin tab uses the UNFILTERED
 * sections so admins can see + unhide them.
 */
export function filterVisibleSections(sections = []) {
  return sections
    .map((s) => ({ ...s, tasks: (s.tasks || []).filter((t) => !t.hidden) }))
    .filter((s) => s.tasks.length > 0);
}

/**
 * Load a single task's raw config.
 * POST { task: "config", name } -> { success, task, config: {...} }.
 * Returns the config object, or null if unavailable.
 */
export const getTaskConfig = async (taskName) => {
  if (!taskName) return null;
  try {
    const data = await dispatch({ task: "config", name: taskName });
    if (data && data.success && data.config) return data.config;
    return null;
  } catch (err) {
    console.warn(`getTaskConfig(${taskName}) failed:`, err?.message);
    return null;
  }
};

/**
 * Save an updated task config back to S3 via the dispatcher.
 * POST { task: "save_config", name, config } -> { success, summary }.
 * Throws on failure so the editor can surface the error.
 */
export const saveTaskConfig = async (taskName, config) => {
  if (!taskName) throw new Error("saveTaskConfig requires a task name");
  if (!config || typeof config !== "object") {
    throw new Error("saveTaskConfig requires a config object");
  }
  const data = await dispatch({ task: "save_config", name: taskName, config });
  if (!data || !data.success) {
    throw new Error(data?.summary || "Failed to save config");
  }
  return data;
};

/**
 * Validate a multi-entry schedule and list its next runs (read-only).
 * POST { task: "schedule_preview", schedules } ->
 *   { success, summary, next_runs: [{utc, local}], runs_today, errors? }.
 */
export const previewSchedule = async (schedules) =>
  dispatch({ task: "schedule_preview", schedules, count: 8 });

/**
 * Admin, one-off: move every task's schedule onto its triggers.
 * POST { task: "migrate_schedules", dry_run } -> { summary, tasks: [{task, status, detail}] }.
 */
export const migrateSchedules = async (dryRun = true) =>
  dispatch({ task: "migrate_schedules", dry_run: !!dryRun });

/**
 * Run a task on demand. By default this is a DRY RUN for safety — the caller
 * must explicitly pass { dryRun: false } to perform a live run.
 * POST { task: name, dry_run } -> dispatcher result { success, summary, ... }.
 */
/**
 * Poll a background run's result by run_id until it finishes.
 * POST { task: "run_result", run_id } -> { status: "running" } | { status:"done", success, summary }.
 */
export const pollRunResult = async (runId) => {
  const data = await dispatch({ task: "run_result", run_id: runId });
  return data || { status: "running" };
};

/**
 * Dispatch a task run and resolve with its FINAL result — even for tasks that
 * take longer than the API Gateway 30s cap.
 *
 * The backend returns { started:true, run_id } immediately (it runs the task in
 * a background self-invoke). We then poll run_result until the task finishes and
 * resolve with the real { success, summary, ... }. If the backend ran the task
 * inline (fallback / fast path), we get the result directly and return it.
 *
 * onProgress("running") is called while waiting so the UI can show a spinner.
 */
const runWithPolling = async (
  startBody,
  { onProgress, firstDelayMs = 6000, intervalMs = 5000, timeoutMs = 15 * 60 * 1000 } = {}
) => {
  const data = await dispatch(startBody);
  // Inline result (fast path / fallback) — not an async start.
  if (!data || !data.started || !data.run_id) return data;

  if (onProgress) onProgress("running");
  const runId = data.run_id;
  const deadline = Date.now() + timeoutMs;
  // Give the background run a head start before the first poll — a task that
  // went async is never done instantly, so polling right away just wastes calls.
  await new Promise((r) => setTimeout(r, firstDelayMs));
  while (Date.now() < deadline) {
    let res;
    try {
      res = await pollRunResult(runId);
    } catch (_) {
      res = null; // transient — keep polling
    }
    if (res && res.status === "done") {
      return { success: !!res.success, summary: res.summary || "", task: res.task, run_id: runId };
    }
    if (onProgress) onProgress("running");
    // Space out the checks so we're not hammering the API every couple seconds.
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { success: false, summary: "Still running after 15 min — check Recent Runs for the result.", run_id: runId };
};

/**
 * Run a task on demand. By default this is a DRY RUN for safety — the caller
 * must explicitly pass { dryRun: false } to perform a live run.
 * Uses start+poll so slow tasks don't hit the 30s gateway cap.
 */
// Optional `tables` (table keys from getTaskTables) limits a run to those tables;
// empty / omitted = every trigger sends what it's configured to send.
const withTables = (cmd, tables) =>
  Array.isArray(tables) && tables.length ? { ...cmd, tables } : cmd;

export const runTask = async (taskName, { dryRun = true, login = "", onProgress, tables } = {}) => {
  if (!taskName) throw new Error("runTask requires a task name");
  return runWithPolling(withTables({ task: taskName, dry_run: dryRun, login }, tables), { onProgress });
};

/**
 * "Test" run: runs the real task but redirects ALL email delivery to the person
 * running it (<login>@amazon.com, worked out server-side from the login; the
 * test_to sent here is kept for older backends only). Uses start+poll.
 */
export const runTaskTest = async (taskName, testTo, login = "", onProgress, tables) => {
  if (!taskName) throw new Error("runTaskTest requires a task name");
  return runWithPolling(withTables({ task: taskName, test: true, test_to: testTo, login }, tables), { onProgress });
};

/**
 * Admin "Test" run for RAPID / SLACK tasks: posts the report to the configured
 * test Slack channel (rapid also forces dry-run). Uses start+poll.
 */
export const runRapidTest = async (taskName, login = "", onProgress, tables) => {
  if (!taskName) throw new Error("runRapidTest requires a task name");
  return runWithPolling(withTables({ task: taskName, test: true, login }, tables), { onProgress });
};

/**
 * Tables a task's report can send (for the trigger + Run-now pickers).
 * POST { task: "list_tables", name } -> { supported, tables: [{key, title, group}] }.
 */
export const getTaskTables = async (taskName) => {
  const data = await dispatch({ task: "list_tables", name: taskName });
  if (!data || data.success === false) throw new Error(data?.summary || "Could not list tables");
  return data;
};

/**
 * Run history (global). POST { task: "list_runs", name?, limit? }.
 * Returns an array of { task, type, timestamp, success, summary } (newest first).
 */
export const listRuns = async ({ name = null, limit = 200 } = {}) => {
  try {
    const data = await dispatch({ task: "list_runs", name: name || undefined, limit });
    return data && Array.isArray(data.runs) ? data.runs : [];
  } catch (err) {
    console.warn("listRuns failed:", err?.message);
    return [];
  }
};

/**
 * Per-task run summary map. POST { task: "run_summary" }.
 * Returns { "<task>": { count, last_run, last_status }, ... }.
 */
export const getRunSummary = async () => {
  try {
    const data = await dispatch({ task: "run_summary" });
    return data && data.summary_map && typeof data.summary_map === "object"
      ? data.summary_map
      : {};
  } catch (err) {
    console.warn("getRunSummary failed:", err?.message);
    return {};
  }
};

/**
 * Load a task's CSV map file. POST { task: "get_map", name, file }.
 * Returns { headers, rows, csv } or null on failure.
 */
export const getMap = async (taskName, file) => {
  if (!taskName || !file) return null;
  try {
    const data = await dispatch({ task: "get_map", name: taskName, file });
    if (data && data.success) {
      return {
        headers: data.headers || [],
        rows: data.rows || [],
        csv: data.csv, // may be undefined; UI can rebuild from headers/rows
      };
    }
    return null;
  } catch (err) {
    console.warn(`getMap(${taskName}/${file}) failed:`, err?.message);
    return null;
  }
};

/**
 * Delete a CUSTOM automation (UI-registered, no code handler): backs up and
 * deletes its config, disables its schedule, removes it from the sidebar.
 * POST { task: "delete_task", name }. Throws on failure.
 */
export const deleteAutomation = async (taskName) => {
  if (!taskName) throw new Error("deleteAutomation requires a task name");
  const data = await dispatch({ task: "delete_task", name: taskName });
  if (!data || !data.success) throw new Error(data?.summary || "Failed to delete automation");
  return data;
};

/** Deleted automations (admin). POST { task: "deleted_tasks" } -> [{name, builtin, deleted_at, deleted_by}]. */
export const getDeletedAutomations = async () => {
  const data = await dispatch({ task: "deleted_tasks" });
  if (!data || !data.success) throw new Error(data?.summary || "Could not load deleted automations");
  return data.deleted || [];
};

/** Restore a deleted automation (admin). POST { task: "restore_task", name }. */
export const restoreAutomation = async (taskName) => {
  const data = await dispatch({ task: "restore_task", name: taskName });
  if (!data || !data.success) throw new Error(data?.summary || "Restore failed");
  return data;
};

/** Activity log weeks (admin). -> { weeks: ["2026-W44", ...], current }. */
export const getActivityWeeks = async () => {
  const data = await dispatch({ task: "activity_weeks" });
  if (!data || !data.success) throw new Error(data?.summary || "Could not load activity log");
  return { weeks: data.weeks || [], current: data.current };
};

/**
 * Revert one logged change (admin). POST { task: "revert_change", week, id, force }.
 * Returns the backend result as-is (incl. `conflict: true` when the item was
 * edited again later), so the UI can ask before forcing.
 */
export const revertChange = async (week, id, force = false) => {
  const data = await dispatch({ task: "revert_change", week, id, force });
  return data || { success: false, summary: "No response" };
};

/** All activity events of one ISO week (admin). */
export const getActivityWeek = async (week) => {
  const data = await dispatch({ task: "activity_week", week });
  if (!data || !data.success) throw new Error(data?.summary || "Could not load activity week");
  return data.events || [];
};

/**
 * Resolved report layout for the Report Design UI (KYC Core).
 * POST { task: "report_layout", name } -> { reports, functions, unassigned, defaults, sources }.
 * Throws on failure so the editor can show the error.
 */
export const getReportLayout = async (taskName) => {
  if (!taskName) throw new Error("getReportLayout requires a task name");
  const data = await dispatch({ task: "report_layout", name: taskName });
  if (!data || !data.success) throw new Error(data?.summary || "Could not load report layout");
  return data;
};

/**
 * Save a task's CSV map file. POST { task: "save_map", name, file, headers, rows }
 * or { ..., csv }. The backend validates the header SET matches the existing
 * file (order-insensitive). Throws on failure so the editor can surface it.
 */
/** Per-run reports (Audit tab) for tasks that save them (e.g. KYC template reset). */
export const getRunReports = async (taskName) => {
  const data = await dispatch({ task: "run_reports", name: taskName });
  if (!data || data.success === false) throw new Error(data?.summary || "Could not load runs");
  return data; // { supported, runs: [...] }
};
export const getRunReport = async (taskName, stamp) => {
  const data = await dispatch({ task: "run_report", name: taskName, stamp });
  if (!data || !data.success) throw new Error(data?.summary || "Could not load the run");
  return data.report;
};

/** Workbook (.xlsx) maps: info / download / upload (validated by the task). */
export const getMapFileInfo = async (taskName, file) =>
  dispatch({ task: "map_file_info", name: taskName, file });

export const downloadMapFile = async (taskName, file) => {
  const data = await dispatch({ task: "get_map_file", name: taskName, file });
  if (!data || !data.success) throw new Error(data?.summary || "Download failed");
  return data; // { content_b64, bytes }
};

export const uploadMapFile = async (taskName, file, fileObj) => {
  const buf = new Uint8Array(await fileObj.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  const data = await dispatch({
    task: "upload_map_file", name: taskName, file, content_b64: btoa(bin), original_name: fileObj.name,
  });
  if (!data || !data.success) throw new Error(data?.summary || "Upload failed");
  return data;
};

export const saveMap = async (taskName, file, payload) => {
  if (!taskName || !file) throw new Error("saveMap requires task and file");
  const body = { task: "save_map", name: taskName, file };
  if (payload && payload.csv != null) body.csv = payload.csv;
  else {
    body.headers = payload?.headers || [];
    body.rows = payload?.rows || [];
  }
  const data = await dispatch(body);
  if (!data || !data.success) {
    throw new Error(data?.summary || "Failed to save map");
  }
  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// RAPID audit (per-day prioritize/deprioritize log)
// ─────────────────────────────────────────────────────────────────────────────

/** Dates (newest first) that have audit data for a task. */
export const getAuditDates = async (taskName) => {
  try {
    const data = await dispatch({ task: "get_audit_dates", name: taskName });
    return data && Array.isArray(data.dates) ? data.dates : [];
  } catch (err) {
    console.warn("getAuditDates failed:", err?.message);
    return [];
  }
};

/** One day's audit record: { date, cycles: [{timestamp, dry_run, prioritized[], deprioritized[], nightly_reset}] }. */
export const getAudit = async (taskName, date) => {
  try {
    const data = await dispatch({ task: "get_audit", name: taskName, date });
    if (data && data.success) return { date: data.date, cycles: data.cycles || [] };
    return { date, cycles: [] };
  } catch (err) {
    console.warn("getAudit failed:", err?.message);
    return { date, cycles: [] };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Task registry (admin add sidebar item)
// ─────────────────────────────────────────────────────────────────────────────

/** The S3 task registry (admin-added entries). */
export const getRegistry = async () => {
  try {
    const data = await dispatch({ task: "get_registry" });
    return data && Array.isArray(data.registry) ? data.registry : [];
  } catch (err) {
    console.warn("getRegistry failed:", err?.message);
    return [];
  }
};

/** Register (upsert) a new automation entry. Throws on failure. */
export const saveRegistryEntry = async (entry) => {
  const data = await dispatch({ task: "save_registry_entry", entry });
  if (!data || !data.success) throw new Error(data?.summary || "Failed to register automation");
  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// Access control
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolve the signed-in user's role for the Automation console.
 * POST { task: "get_permissions", login } -> { role, allowed_tasks }.
 * Returns { role: "no_access", allowed_tasks: [] } on failure.
 */
export const getAutoPermissions = async (login) => {
  try {
    const data = await dispatch({ task: "get_permissions", login });
    if (data && data.success) {
      return {
        role: data.role || "no_access",
        allowed_tasks: data.allowed_tasks || [],
        is_owner: !!data.is_owner,
      };
    }
    return { role: "no_access", allowed_tasks: [], is_owner: false };
  } catch (err) {
    console.warn("getAutoPermissions failed:", err?.message);
    return { role: "no_access", allowed_tasks: [], is_owner: false };
  }
};

/** GET the full access list. POST { task: "get_access_list" }. */
export const getAutoAccessList = async () => {
  try {
    const data = await dispatch({ task: "get_access_list" });
    return data && Array.isArray(data.access_list) ? data.access_list : [];
  } catch (err) {
    console.warn("getAutoAccessList failed:", err?.message);
    return [];
  }
};

/** Save the access list. POST { task: "save_access_list", access_list }. Throws on failure. */
export const saveAutoAccessList = async (accessList) => {
  const data = await dispatch({ task: "save_access_list", access_list: accessList });
  if (!data || !data.success) throw new Error(data?.summary || "Failed to save access list");
  return data;
};

/**
 * Read-only backend health check. POST { task: "selftest" }.
 */
export const runSelfTest = async () => {
  try {
    return await dispatch({ task: "selftest" });
  } catch (err) {
    return { success: false, summary: err?.message || "selftest failed" };
  }
};

/**
 * Load the raw global config (secrets stay as ${ENV} tokens).
 * POST { task: "get_global" } -> { success, config }.
 */
export const getGlobalConfig = async (login = "") => {
  // Throws with the backend's reason (e.g. "restricted to owners") so the Auth
  // tab can show WHY it couldn't load, instead of a blank page.
  const data = await dispatch({ task: "get_global", login });
  if (data && data.success && data.config) return data.config;
  throw new Error(data?.summary || "Could not load global config");
};

/**
 * Save the global config back to S3. POST { task: "save_global", config }.
 * Throws on failure.
 */
export const saveGlobalConfig = async (config, login = "") => {
  if (!config || typeof config !== "object") throw new Error("saveGlobalConfig requires a config object");
  const data = await dispatch({ task: "save_global", config, login });
  if (!data || !data.success) throw new Error(data?.summary || "Failed to save global config");
  return data;
};

/**
 * Clear run history. POST { task: "clear_runs", name? }.
 * If name is given, clears only that task's runs; otherwise wipes all history.
 * Throws on failure.
 */
export const clearRunHistory = async (taskName = null) => {
  const data = await dispatch({ task: "clear_runs", name: taskName || undefined });
  if (!data || !data.success) throw new Error(data?.summary || "Failed to clear run history");
  return data;
};

/**
 * Read-only service-cookie status. POST { task: "cookie_status" }.
 * Returns an array of per-service entries:
 *   { service, s3_key, validity_cookie, present, valid, reason,
 *     valid_until, remaining_minutes, cookies: [{name, alive, valid_until, remaining_minutes}] }
 */
export const getCookieStatus = async (login = "") => {
  try {
    const data = await dispatch({ task: "cookie_status", login });
    return {
      cookies: data && Array.isArray(data.cookies) ? data.cookies : [],
      checked_at: data?.checked_at || null,
      // Backend refusal/failure reason (e.g. "restricted to owners"), if any.
      error: data && data.success === false ? data.summary || "Cookie status failed" : null,
    };
  } catch (err) {
    console.warn("getCookieStatus failed:", err?.message);
    return { cookies: [], checked_at: null, error: err?.message || "Cookie status failed" };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Task classification + per-task sub-sections (for the accordion sidebar)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Classify a task by the SHAPE of its config, since the backend configs carry
 * no explicit "type" field:
 *   - "rapid"    : has rcc + arrm   -> real-time queue prioritization (writes to Paragon)
 *   - "mail"     : delivers email (triggers[].email, legacy email, or groups[].email)
 *   - "forecast" : has queue_types|folders|nudge -> monitoring/Slack
 *   - "generic"  : fallback
 * Delivery now lives under `triggers[]` (each trigger has an `email` or `slack`
 * block); older configs may still have a flat `email`/`slack` or a `groups`
 * array. All are handled. Falls back to the name prefix when config is null.
 */
export function classifyTask(config, taskName = "") {
  const cfg = config || {};
  const trigs = Array.isArray(cfg.triggers) ? cfg.triggers : [];
  const anyTrigger = (key) => trigs.some((t) => t && t[key]);

  // RAPID is unambiguous by its rcc+arrm config OR its name — check first so a
  // rapid task (whose delivery trigger is slack) isn't misread as slack/forecast.
  if ((cfg.rcc && cfg.arrm) || taskName.endsWith("_rapid") || taskName === "vat_rapid")
    return "rapid";
  // Forecast monitoring tasks (distinct shape).
  if (cfg.queue_types || cfg.folders || cfg.nudge) return "forecast";
  // Delivery-driven: a QSR/report is either MAIL (email) or SLACK (channel).
  // Check email first; a task with BOTH email+slack triggers counts as mail.
  if (cfg.email || anyTrigger("email") || Array.isArray(cfg.groups)) return "mail";
  if (cfg.slack || anyTrigger("slack")) return "slack";
  // Name fallbacks when config isn't loaded yet.
  if (taskName.includes("_mail")) return "mail";
  if (taskName === "vat_hourly_qsr") return "slack";
  if (taskName.startsWith("forecast")) return "forecast";
  return "generic";
}

/** Normalize a task's triggers for display. Returns [{ id, email?, slack? }].
 *  Falls back to a single synthesized trigger from a legacy flat email/slack. */
export function getTriggers(config) {
  const cfg = config || {};
  if (Array.isArray(cfg.triggers) && cfg.triggers.length) {
    return cfg.triggers.map((t, i) => ({ id: (t && t.id) || `trigger_${i + 1}`, ...t }));
  }
  const legacy = { id: "default" };
  if (cfg.email) legacy.email = cfg.email;
  if (cfg.slack) legacy.slack = cfg.slack;
  return [legacy];
}

